import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    hasInternalAdminPrivileges,
    type ActiveRoleAssignmentResponse,
    PaymentRequestStatus,
    getPaymentRequestReminderUnavailableReason,
    ResendPaymentRequestRequestSchema,
    type PaymentRequestDetailsView,
    type ResendPaymentRequestRequest,
    type FinalizeSendPaymentRequestResponse,
} from '@pillar-portal/api-types';
import {
    AuthorizableAction,
    ResourceType,
} from '@pillar-portal/database/dist/authorizable-actions';
import { ApiError, authApi, authorizationApi, paymentsApi } from '@/lib/pillarApi';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PaymentRequestDetailView } from '../PaymentRequestDetailView';
import {
    appendMerchantOrganizationUuid,
    getMerchantOrganizationUuidFromSearchParams,
} from '../merchant-organization-search';

const revocablePaymentRequestStatuses: ReadonlySet<string> = new Set([
    PaymentRequestStatus.SENT,
    PaymentRequestStatus.VIEWED,
    PaymentRequestStatus.PARTIALLY_PAID,
    PaymentRequestStatus.REISSUED,
]);

function getRevokeDisabledReason(params: {
    isRevoking: boolean;
    isAuthorized: boolean | null | undefined;
}): string | null {
    if (params.isRevoking) {
        return 'Revoke in progress.';
    }
    if (params.isAuthorized == null) {
        return 'Checking permissions.';
    }
    if (params.isAuthorized !== true) {
        return 'This functionality is disabled because you have not been granted the appropriate permission.';
    }
    return null;
}

function isRevocablePaymentRequestStatus(status: string): boolean {
    return revocablePaymentRequestStatuses.has(status);
}

export function MerchantPaymentRequestDetailContent() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState<boolean>(false);
    const paymentRequestUuid = params.uuid;
    const merchantOrganizationUuid: string | undefined =
        getMerchantOrganizationUuidFromSearchParams(searchParams);

    if (paymentRequestUuid === undefined || paymentRequestUuid.trim().length === 0) {
        throw new Error('Missing payment request UUID');
    }

    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const activeRoleAssignmentOperationalResponse = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentQuery.data
    );
    const identity = `${paymentRequestUuid}/${merchantOrganizationUuid ?? 'active-role'}/${activeRoleAssignmentOperationalResponse?.role_assignment_uuid ?? 'no-role'}`;
    const identityRef = useRef(identity);
    identityRef.current = identity;
    const [reminderEditor, setReminderEditor] = useState<{
        identity: string;
        recipientEmail: string | null;
        recipientPhone: string | null;
        sendEmail: boolean | null;
        sendSms: boolean | null;
        isSending: boolean;
        finalizeSendPaymentRequestResponse: FinalizeSendPaymentRequestResponse | null;
    }>({
        identity,
        recipientEmail: null,
        recipientPhone: null,
        sendEmail: null,
        sendSms: null,
        isSending: false,
        finalizeSendPaymentRequestResponse: null,
    });
    if (reminderEditor.identity !== identity) {
        setReminderEditor({
            identity,
            recipientEmail: null,
            recipientPhone: null,
            sendEmail: null,
            sendSms: null,
            isSending: false,
            finalizeSendPaymentRequestResponse: null,
        });
    }
    const paymentRequestQuery = useQuery<PaymentRequestDetailsView>({
        queryKey: [
            'merchantPaymentRequestDetails',
            paymentRequestUuid,
            merchantOrganizationUuid ?? 'active-role',
            activeRoleAssignmentOperationalResponse?.role_assignment_uuid,
        ],
        queryFn: async () =>
            merchantOrganizationUuid === undefined
                ? await paymentsApi.getMerchantPaymentRequestDetails(paymentRequestUuid)
                : await paymentsApi.getMerchantPaymentRequestDetails(paymentRequestUuid, {
                      merchant_organization_uuid: merchantOrganizationUuid,
                  }),
        retry: false,
        enabled: !activeRoleAssignmentQuery.isPending,
    });
    const queryClient = useQueryClient();
    const reminderChannelsQuery = useQuery({
        queryKey: [
            'paymentRequestReminderChannels',
            identity,
            paymentRequestQuery.data?.merchant_organization_uuid,
        ],
        queryFn: async () => {
            if (
                activeRoleAssignmentOperationalResponse === null ||
                paymentRequestQuery.data === undefined
            ) {
                throw new Error('An operational role and payment request are required.');
            }
            return await paymentsApi.getPaymentRequestDeliveryChannels(
                activeRoleAssignmentOperationalResponse,
                paymentRequestQuery.data.merchant_organization_uuid
            );
        },
        enabled:
            activeRoleAssignmentOperationalResponse !== null &&
            paymentRequestQuery.data !== undefined,
        retry: false,
    });
    const reminderAuthorizationQuery = useQuery({
        queryKey: ['paymentRequestEditAuthorization', identity],
        queryFn: async () =>
            await authorizationApi.isAuthorized({
                action: AuthorizableAction.EDIT,
                resource_type: ResourceType.PAYMENT_REQUEST,
                target_resource: {
                    designator_type: ResourceType.PAYMENT_REQUEST,
                    payment_request_uuid: paymentRequestUuid,
                },
            }),
        enabled: activeRoleAssignmentOperationalResponse !== null,
        retry: false,
    });
    const reminderMutation = useMutation({
        mutationFn: async (variables: {
            identity: string;
            paymentRequestUuid: string;
            merchantOrganizationUuid: string;
            resendPaymentRequestRequest: ResendPaymentRequestRequest;
        }) =>
            await paymentsApi.resendPaymentRequest(
                variables.paymentRequestUuid,
                variables.resendPaymentRequestRequest,
                { merchant_organization_uuid: variables.merchantOrganizationUuid }
            ),
        onMutate: (variables) => {
            setReminderEditor((previous) =>
                previous.identity === variables.identity
                    ? { ...previous, isSending: true }
                    : previous
            );
        },
        onError: (_error, variables) => {
            setReminderEditor((previous) =>
                previous.identity === variables.identity
                    ? { ...previous, isSending: false }
                    : previous
            );
        },
        onSuccess: async (finalizeSendPaymentRequestResponse, variables) => {
            if (identityRef.current !== variables.identity) return;
            setReminderEditor((previous) =>
                previous.identity === variables.identity
                    ? { ...previous, isSending: false, finalizeSendPaymentRequestResponse }
                    : previous
            );
            await Promise.allSettled([
                paymentRequestQuery.refetch(),
                queryClient.resetQueries({
                    queryKey: [
                        'paymentRequestDeliveryEvents',
                        variables.paymentRequestUuid,
                        variables.merchantOrganizationUuid,
                        activeRoleAssignmentOperationalResponse?.role_assignment_uuid,
                    ],
                }),
            ]);
        },
    });
    const revokeAuthorizationQuery = useQuery({
        queryKey: ['paymentRequestDeleteAuthorization', paymentRequestUuid],
        queryFn: async () =>
            await authorizationApi.isAuthorized({
                action: AuthorizableAction.DELETE,
                resource_type: ResourceType.PAYMENT_REQUEST,
                target_resource: {
                    designator_type: ResourceType.PAYMENT_REQUEST,
                    payment_request_uuid: paymentRequestUuid,
                },
            }),
        staleTime: 30000,
    });
    const revokeMutation = useMutation({
        mutationFn: async () =>
            merchantOrganizationUuid === undefined
                ? await paymentsApi.revokePaymentRequestLink(paymentRequestUuid)
                : await paymentsApi.revokePaymentRequestLink(paymentRequestUuid, {
                      merchant_organization_uuid: merchantOrganizationUuid,
                  }),
        onSuccess: async () => {
            setIsRevokeConfirmOpen(false);
            await queryClient.invalidateQueries({
                queryKey: [
                    'merchantPaymentRequestDetails',
                    paymentRequestUuid,
                    merchantOrganizationUuid ?? 'active-role',
                ],
            });
        },
    });

    if (paymentRequestQuery.isLoading && paymentRequestQuery.data === undefined) {
        return <div>Loading payment request details...</div>;
    }

    if (paymentRequestQuery.data === undefined) {
        const errorMessage =
            paymentRequestQuery.error instanceof ApiError
                ? paymentRequestQuery.error.message
                : 'Failed to load payment request details.';

        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load payment request</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const paymentRequestDetailsView = paymentRequestQuery.data;
    const recipientEmail =
        reminderEditor.recipientEmail ?? paymentRequestDetailsView.recipient_email ?? '';
    const recipientPhone =
        reminderEditor.recipientPhone ?? paymentRequestDetailsView.recipient_phone ?? '';
    const emailAllowed = reminderChannelsQuery.data?.includes('EMAIL') === true;
    const smsAllowed = reminderChannelsQuery.data?.includes('SMS') === true;
    const sendEmail = emailAllowed && (reminderEditor.sendEmail ?? true);
    const sendSms = smsAllowed && (reminderEditor.sendSms ?? !emailAllowed);
    const resendPaymentRequestRequest: ResendPaymentRequestRequest | null =
        sendEmail && sendSms
            ? {
                  send_email: true,
                  send_sms: true,
                  recipient_email: recipientEmail,
                  recipient_phone: recipientPhone,
              }
            : sendEmail
              ? { send_email: true, send_sms: false, recipient_email: recipientEmail }
              : sendSms
                ? { send_email: false, send_sms: true, recipient_phone: recipientPhone }
                : null;
    const recipientValid =
        resendPaymentRequestRequest !== null &&
        ResendPaymentRequestRequestSchema.safeParse(resendPaymentRequestRequest).success;
    const reminderPending = reminderEditor.identity === identity && reminderEditor.isSending;
    const reminderDisabledReason = reminderPending
        ? 'Reminder dispatch in progress.'
        : (getPaymentRequestReminderUnavailableReason(paymentRequestDetailsView) ??
          (activeRoleAssignmentOperationalResponse === null
              ? 'An operational role is required.'
              : reminderAuthorizationQuery.isPending
                ? 'Checking EDIT permission.'
                : reminderAuthorizationQuery.error !== null
                  ? 'Unable to check EDIT permission.'
                  : reminderAuthorizationQuery.data?.is_authorized !== true
                    ? 'EDIT permission is required to send a reminder.'
                    : reminderChannelsQuery.error !== null
                      ? 'Unable to load delivery channels.'
                      : reminderChannelsQuery.isPending
                        ? 'Loading delivery channels.'
                        : !emailAllowed && !smsAllowed
                          ? 'No reminder delivery channel is configured.'
                          : !recipientValid
                            ? 'Select a configured channel and enter a valid recipient.'
                            : null));
    const reminderResponse =
        reminderEditor.identity === identity
            ? reminderEditor.finalizeSendPaymentRequestResponse
            : null;
    const reminderAction = (
        <section aria-label="Payment reminder" className="space-y-2">
            {emailAllowed && (
                <div className="block">
                    <label>
                        <input
                            type="checkbox"
                            aria-label="Send email reminder"
                            checked={sendEmail}
                            disabled={reminderPending}
                            onChange={(event) =>
                                setReminderEditor({
                                    ...reminderEditor,
                                    sendEmail: event.target.checked,
                                })
                            }
                        />{' '}
                        Email
                    </label>
                    <Input
                        aria-label="Reminder email"
                        value={recipientEmail}
                        disabled={reminderPending}
                        onChange={(event) =>
                            setReminderEditor({
                                ...reminderEditor,
                                recipientEmail: event.target.value,
                            })
                        }
                    />
                </div>
            )}
            {smsAllowed && (
                <div className="block">
                    <label>
                        <input
                            type="checkbox"
                            aria-label="Send SMS reminder"
                            checked={sendSms}
                            disabled={reminderPending}
                            onChange={(event) =>
                                setReminderEditor({
                                    ...reminderEditor,
                                    sendSms: event.target.checked,
                                })
                            }
                        />{' '}
                        SMS
                    </label>
                    <Input
                        aria-label="Reminder phone"
                        value={recipientPhone}
                        disabled={reminderPending}
                        onChange={(event) =>
                            setReminderEditor({
                                ...reminderEditor,
                                recipientPhone: event.target.value,
                            })
                        }
                    />
                </div>
            )}
            <Button
                disabled={reminderDisabledReason !== null}
                onClick={() => {
                    if (
                        reminderDisabledReason !== null ||
                        resendPaymentRequestRequest === null ||
                        getPaymentRequestReminderUnavailableReason(paymentRequestDetailsView) !==
                            null
                    )
                        return;
                    reminderMutation.mutate({
                        identity,
                        paymentRequestUuid,
                        merchantOrganizationUuid:
                            paymentRequestDetailsView.merchant_organization_uuid,
                        resendPaymentRequestRequest,
                    });
                }}
            >
                {reminderPending ? 'Sending reminder...' : 'Send reminder'}
            </Button>
            {reminderDisabledReason !== null && <p>{reminderDisabledReason}</p>}
            {reminderMutation.variables?.identity === identity &&
                reminderMutation.error !== null && (
                    <p role="alert">{reminderMutation.error.message}</p>
                )}
            {reminderResponse?.channel_results.map((result) => (
                <p role="status" key={result.delivery_channel}>
                    {result.delivery_channel}:{' '}
                    {result.accepted
                        ? 'Dispatch accepted'
                        : `Dispatch failed: ${result.error_message ?? result.error_code ?? 'Provider did not accept dispatch'}`}
                </p>
            ))}
        </section>
    );
    const revokeDisabledReason: string | null = getRevokeDisabledReason({
        isRevoking: revokeMutation.isPending,
        isAuthorized: revokeAuthorizationQuery.data?.is_authorized,
    });
    const canDisplayRevokeAction = isRevocablePaymentRequestStatus(paymentRequestQuery.data.status);
    const revokeAction = canDisplayRevokeAction ? (
        <div title={revokeDisabledReason ?? undefined} className="inline-block">
            <Button
                variant="destructive"
                disabled={revokeDisabledReason !== null}
                onClick={() => {
                    setIsRevokeConfirmOpen(true);
                }}
            >
                {revokeMutation.isPending ? 'Revoking...' : 'Revoke E-Invoice'}
            </Button>
        </div>
    ) : undefined;

    return (
        <>
            {paymentRequestQuery.error !== null && (
                <Alert variant="destructive">
                    <AlertTitle>Unable to refresh payment request</AlertTitle>
                    <AlertDescription>
                        Current details and accepted dispatch outcomes are retained.
                        <Button
                            onClick={async () => {
                                await paymentRequestQuery.refetch();
                            }}
                        >
                            Retry details
                        </Button>
                    </AlertDescription>
                </Alert>
            )}
            <PaymentRequestDetailView
                paymentRequest={paymentRequestQuery.data}
                merchantOrganizationUuid={paymentRequestQuery.data.merchant_organization_uuid}
                operationalRoleAssignmentUuid={
                    getActiveRoleAssignmentOperationalResponse(activeRoleAssignmentQuery.data)
                        ?.role_assignment_uuid
                }
                pageDescription="Merchant payment-request details"
                backHref={appendMerchantOrganizationUuid(
                    '/payment-requests',
                    merchantOrganizationUuid
                )}
                backTooltip="Back to Payment Requests"
                actions={
                    <>
                        {reminderAction}
                        {revokeAction}
                    </>
                }
                showPaymentTransactions
                showSupportDetails={hasInternalAdminPrivileges(
                    getActiveRoleAssignmentOperationalResponse(activeRoleAssignmentQuery.data)
                )}
            />
            <ConfirmationModal
                open={isRevokeConfirmOpen}
                onOpenChange={setIsRevokeConfirmOpen}
                title="Revoke E-Invoice"
                message="This disables the current payment link."
                confirmLabel="Revoke"
                isLoading={revokeMutation.isPending}
                onConfirm={() => {
                    revokeMutation.mutate();
                }}
            />
        </>
    );
}
